import { Seaport } from "@opensea/seaport-js";
import axios, { AxiosResponse } from "axios";
import { ethers } from "ethers";
import React, { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import styled from "styled-components";
import ComposeNFTArtifact from "../artifacts/contracts/ComposeNFT.sol/ComposeNFT.json";
import SongDTO from "../common/dto/SongDTO";
import SimpleButton from "../components/Buttons/SimpleButton";
import Column from "../components/Column/Column";
import { NavBar } from "../components/NavBar";
import RecordProgressBar from "../components/ProgressBar/RecordProgressBar";
import Row from "../components/Row/Row";
import Text from "../components/Text/Text";
import { Timeline } from "../components/Timeline/Timeline";
import { useAuth } from "../hooks/useAuth";
import error_record from "../imgs/error_record.svg";
import light_minting_progress_bg from "../imgs/light_minting_progress_bg.png";
import { getSelectedProvider, sleep } from "../utils";
import minting_progress_bg from "./imgs/minting_progress_page_bg.png";
import { CreateOrderInput } from "@opensea/seaport-js/lib/types";
import { ItemType } from "@opensea/seaport-js/lib/constants";

interface MintingProgressPage {
  theme: string;
  toggleTheme: () => void;
}
interface MintingProgressPageState {
  songDto: SongDTO;
  timelineEvents: Array<string>;
  listLater: boolean;
  listAtScheduledDate?: boolean;
}

const MintingProgressPageContainer = styled.div`
  display: flex;
  flex-direction: column;
  background: url(${(props) =>
      props.theme.themeType === "dark"
        ? minting_progress_bg
        : light_minting_progress_bg})
    no-repeat center;
  background-size: cover;
  width: 100vw;
  height: 100vh;
  justify-content: center;
  align-items: center;
  position: relative;
`;

const BigLetter = styled.h1`
  font-size: 150px;
  padding: 10px;
`;

const ActionContainer = styled.div`
  position: absolute;
  bottom: 25%;
  background: #9d7cff;
  padding: 1%;
  opacity: 0.9;
  border-radius: 16px;
  margin-top: 5%;
`;

const ErrorContainer = styled(Column)`
  position: absolute;
  bottom: 20%;
  background: #932b95;
  opacity: 0.9;
  border-radius: 16px;
  padding-block: 2%;
  padding-inline: 5%;
  margin-top: 5%;
`;

export const MintingProgressPage: React.FC<MintingProgressPage> = ({
  theme,
  toggleTheme,
}) => {
  const [loading, setLoading] = useState(true);
  const [progressText, setProgressText] = useState("");
  const [error, setError] = useState(false);
  const [showAction, setShowAction] = useState(false);
  const { state } = useLocation();
  const locationState = state as MintingProgressPageState;
  const navigate = useNavigate();
  const auth = useAuth();
  const wrappedEthTestNetAddress = "0xA6FA4fB5f76172d178d61B04b0ecd319C5d1C0aa";
  const wrappedEthMainnetAddress = "0x7ceB23fD6bC0adD59E62ac25578270cFf1b9f619";
  const isMainnet = process.env.REACT_APP_USE_POLYGON_MAINNET === "true";

  useEffect(() => {
    if (locationState.listLater) {
      deployNFTContract();
    } else {
      deployNFTAndListOnSeaport();
    }
  }, []);

  const deployNFTContract = () => {
    const songDto = locationState.songDto;
    if (auth && auth.user && window.ethereum) {
      const selectedProvider = getSelectedProvider();
      const provider = new ethers.providers.Web3Provider(selectedProvider);
      const contract = new ethers.ContractFactory(
        ComposeNFTArtifact.abi,
        ComposeNFTArtifact.bytecode,
        provider.getSigner()
      );

      deployAndMint(provider, contract, songDto);
    }
  };

  const deployAndMint = async (provider, contract, songDto) => {
    const baseUri = await uploadToIpfs(songDto);
    const resContract = await deploySmartContract(
      provider,
      contract,
      songDto.songName,
      songDto.songName.substring(0, 3),
      baseUri
    );

    const composeNFTContract = new ethers.Contract(
      resContract.address,
      ComposeNFTArtifact.abi,
      provider.getSigner()
    );

    console.log('tokenId: 1 owner of is ', await composeNFTContract.ownerOf(1))
    console.log('tokenId: 1 owner of is ', await composeNFTContract.ownerOf('1'))

    setProgressText("Successfully minted NFT!");
    addMasterCopy(songDto, baseUri, resContract, null)
  };

  const addMasterCopy = (songDto, baseUri, resContract, startTime) => {

    const formData = new FormData();
    formData.append("wallet_address", auth.user.wallet_address);
    if (songDto.songCoverImg) {
      formData.append("song_cover", songDto.songCoverImg);
    }
    formData.append("song_name", songDto.songName);
    formData.append("description", songDto.description);
    formData.append("for_sale", "false");
    formData.append("nft_address", resContract.address);
    formData.append("price", songDto.price);
    formData.append("song", songDto.songFile);
    formData.append("spotify_url", songDto.spotifyUrl);
    formData.append("youtube_url", songDto.youtubeUrl);
    formData.append("full_song_length", songDto.fullSongLength.toString());
    if (startTime !== null) {
      formData.append("start_time", startTime);
    }
    formData.append("metadata_base_url", baseUri);

    axios
      .post(process.env.REACT_APP_BACKEND_URL + "/add_master_copy", formData, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      })
      .then((response: AxiosResponse) => {
        setProgressText("Successfully created and minted NFT!");
        setLoading(false);
      })
      .catch(() => {
        setError(true);
        setProgressText("Sorry, there was an error. Please try again ");
      });

  }

  const deployNFTAndListOnSeaport = () => {
    const songDto = locationState.songDto;
    if (auth && auth.user && window.ethereum) {
      const selectedProvider = getSelectedProvider();
      const provider = new ethers.providers.Web3Provider(selectedProvider);
      const contract = new ethers.ContractFactory(
        ComposeNFTArtifact.abi,
        ComposeNFTArtifact.bytecode,
        provider.getSigner()
      );
      deployMintCreateOrder(provider, contract, songDto);
    }
  };

  const deployMintCreateOrder = async (provider, contract, songDto) => {
    const baseUri = await uploadToIpfs(songDto);
    const resContract = await deploySmartContract(
      provider,
      contract,
      songDto.songName,
      songDto.songName.substring(0, 3),
      baseUri
    );
    const composeNFTContract = new ethers.Contract(
      resContract.address,
      ComposeNFTArtifact.abi,
      provider.getSigner()
    );

    setProgressText("Successfully minted NFT!");

    console.log('tokenId: 1 owner of is ', await composeNFTContract.ownerOf(1))
    console.log('tokenId: 1 owner of is ', await composeNFTContract.ownerOf('1'))
    await createOrder(provider, songDto, resContract, baseUri);
  };

  const createOrder = async (provider, songDto, resContract, baseUri) => {
    const seller = await provider.getSigner().getAddress();
    const seaport = new Seaport(provider);
    let startTime = null;
    if (locationState.listAtScheduledDate) {
      startTime = songDto.launchDate;
    }

    const standardCreateOrderInput: CreateOrderInput = {
      startTime: startTime === null ? "0" : startTime.toString(),
      offer: [
        {
          itemType: ItemType.ERC721,
          token: resContract.address,
          identifier: "1",
        },
      ],
      // Use wrapped ether instead of the native currency on chain

      consideration: [
        {
          amount: ethers.utils.parseEther(songDto.price).toString(),
          token: isMainnet ? wrappedEthMainnetAddress : wrappedEthTestNetAddress,
          recipient: seller,
        },
      ],
      fees: [
        {
          recipient: process.env.REACT_APP_COMPOSENFT_BANK_ADDRESS,
          basisPoints: parseInt(process.env.REACT_APP_NFT_REVENUE_FEE),
        }
      ],
    };


    let listedToSeaport = false;
    let order = null;

    try {

      const { executeAllActions } = await seaport.createOrder(
        standardCreateOrderInput,
        seller
      );
      setProgressText("Listing on marketplace");
      setShowAction(true);
      order = await executeAllActions();
      listedToSeaport = true;
    } catch (error) {
      console.log('there was an error listing to seaport ', error);
    }

    setProgressText("Finalizing listing!");
    setShowAction(false);

    if (listedToSeaport) {
      addMasterCopyWithSeaportOrder(songDto, baseUri, order, startTime, resContract);
    } else {
      addMasterCopy(songDto, baseUri, resContract, startTime)
    }
  };

  const addMasterCopyWithSeaportOrder = (songDto, baseUri, order, startTime, resContract) => {
    const formData = new FormData();
    formData.append("wallet_address", auth.user.wallet_address);
    if (songDto.songCoverImg) {
      formData.append("song_cover", songDto.songCoverImg);
    }
    formData.append("song_name", songDto.songName);
    formData.append("description", songDto.description);
    formData.append("for_sale", "true");
    formData.append("nft_address", resContract.address);
    formData.append("price", songDto.price);
    formData.append("song", songDto.songFile);
    formData.append("spotify_url", songDto.spotifyUrl);
    formData.append("youtube_url", songDto.youtubeUrl);
    formData.append("full_song_length", songDto.fullSongLength.toString());
    formData.append("metadata_base_url", baseUri);

    formData.append("order", JSON.stringify(order));
    formData.append("fees_basis_points", process.env.REACT_APP_NFT_REVENUE_FEE);
    formData.append(
      "fees_wallet_address",
      process.env.REACT_APP_COMPOSENFT_BANK_ADDRESS
    );
    if (startTime !== null) {
      formData.append("start_time", startTime);
    }
    axios
      .post(process.env.REACT_APP_BACKEND_URL + "/add_master_copy", formData, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      })
      .then((response: AxiosResponse) => {
        setProgressText("Successfully uploaded to marketplace!");
        setLoading(false);
      })
      .catch(() => {
        setError(true);
        setProgressText("Sorry, there was an error. Please try again ");
      });
  }

  // const mintNFT = async (provider, composeNFTContract) => {
  //   setProgressText("Minting NFT!");
  //   setShowAction(true);
  //   const address = await provider.getSigner().getAddress();
  //   await composeNFTContract.mint(address);
  // };

  const uploadToIpfs = async (songDto: SongDTO) => {
    setProgressText("Uploading song to IPFS");

    const formData = new FormData();
    const songFilename = songDto.songFile.name.replaceAll(' ', '_');
    const songCoverFilename = songDto.songCoverImg.name.replaceAll(' ', '_');
    formData.append(songFilename, songDto.songFile);
    formData.append('song_file_name', songFilename);
    formData.append("song_cover_file_name", songCoverFilename);
    formData.append(songCoverFilename, songDto.songCoverImg);

    formData.append("name", songDto.songName);
    formData.append("description", songDto.description);
    formData.append("max_tokens", songDto.quantity);

    const response = await axios.post(process.env.REACT_APP_BACKEND_URL + '/create_nft_metadata', formData, {
      headers: {
        "Content-Type": "multipart/form-data",
      }
    })
    return response.data.ipfs_metadata_link;
  };


  const deploySmartContract = async (
    provider,
    contract,
    contractName,
    contractSymbol,
    baseURI
  ) => {
    setProgressText("Deploying Smart Contract");
    setShowAction(true);
    let resContract = null;
    try {
      resContract = await contract.deploy(
        contractName,
        contractSymbol,
        1,
        baseURI
      );
    } catch (error) {
      if (error.code === "INSUFFICIENT_FUNDS") {
        setProgressText("Insufficient funds to deploy contract");
      } else if (error.code === "ACTION_REJECTED") {
        setProgressText("Rejected transaction, please try again");
      } else {
        setProgressText("Error deploying contract, please try again");
      }
      setError(true);
      return;
    }

    // Check every 5 seconds to see if the contract has been deployed
    while (true) {
      setProgressText("Thanks! Continuing to deploy smart contract");
      setShowAction(false);
      await sleep(1000);
      const currentTransaction = await provider.getTransaction(
        resContract.deployTransaction.hash
      );
      console.log('currentTransaction is ', currentTransaction);
      if (
        currentTransaction === null ||
        currentTransaction.blockNumber !== null
      ) {
        // Transaction has been deployed when there is a block number associated with it
        break;
      }
    }

    return resContract;
  };

  const onRetryClick = () => {
    setError(false);
    if (locationState.listLater) {
      deployNFTContract();
    } else {
      deployNFTAndListOnSeaport();
    }
  };

  const onFinishClicked = () => {
    navigate("/user/" + auth.user.wallet_address);
  };
  return (
    <>
      <NavBar theme={theme} themeToggler={toggleTheme} hideNavLinks={true} />
      <MintingProgressPageContainer>
        <Timeline
          events={locationState.timelineEvents}
          currentEventIndex={4}
          customCurrentTitle={"Uploading"}
          header={"Creating Master Copy NFT"}
        />
        {error ? (
          <>
            <Row style={{ maxHeight: "15vh" }}>
              <BigLetter>L</BigLetter>
              <img src={error_record} style={{ width: "200px" }} />

              <BigLetter>A</BigLetter>
              <BigLetter>D</BigLetter>
              <BigLetter>I</BigLetter>
              <BigLetter>N</BigLetter>
              <BigLetter>G</BigLetter>
            </Row>
            <ErrorContainer>
              <Text>{progressText}</Text>
              <SimpleButton
                onClick={onRetryClick}
                style={{
                  maxWidth: "50%",
                  marginLeft: "auto",
                  marginRight: "auto",
                }}
              >
                Retry
              </SimpleButton>
            </ErrorContainer>
          </>
        ) : (
          <>
            <Row style={{ maxHeight: "15vh" }}>
              <BigLetter>L</BigLetter>
              <RecordProgressBar loading={loading} strokeWidth={4} />
              <BigLetter>A</BigLetter>
              <BigLetter>D</BigLetter>
              <BigLetter>I</BigLetter>
              <BigLetter>N</BigLetter>
              <BigLetter>G</BigLetter>
            </Row>

            <Text>{progressText}</Text>
            {showAction && (
              <ActionContainer>
                Please sign the transaction in wallet to continue
              </ActionContainer>
            )}
            {!loading && (
              <SimpleButton onClick={onFinishClicked}>Finish</SimpleButton>
            )}
          </>
        )}
      </MintingProgressPageContainer>
    </>
  );
};
